import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

const AUTH_STORAGE_KEY = 'passbook_admin_auth';

export type AuthFlowType = 'SIGN_IN' | 'SIGN_UP';

export interface Staff {
  staffId: string;
  businessId: string | null;
  token?: string | null;
  name?: string | null;
  contactDetails?: {
    countryCode?: string | null;
    mobile?: string | null;
    email?: string | null;
    contactPerson?: string | null;
  } | null;
  [key: string]: unknown;
}

export interface StoredProfile {
  identifier: string;
  name: string;
  countryCode: string;
  mobile: string;
  email: string;
  token: string;
}

export interface Business {
  erpDomainName: string | null;
  [key: string]: unknown;
}

export interface VerifyOtpResponse {
  data: {
    staff: Staff;
    business: Business | null;
  };
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly http = inject(HttpClient);
  private readonly authenticated = signal(this.readStoredAuth());
  private pendingIdentifier: string | null = null;
  private pendingSignupIdentifier: string | null = null;

  readonly isLoggedIn = this.authenticated.asReadonly();

  isAuthenticated(): boolean {
    return this.authenticated();
  }

  /** Requests an OTP for both login (SIGN_IN) and account creation (SIGN_UP). */
  requestOtp(identifier: string, flowType: AuthFlowType): Observable<unknown> {
    return this.http.post(`${environment.baseUrl}staff/ssp/signIn`, {
      identifier: identifier.trim(),
      flowType,
    });
  }

  markOtpRequested(identifier: string, flowType: AuthFlowType): void {
    const normalizedIdentifier = identifier.trim().toLowerCase();
    if (flowType === 'SIGN_IN') {
      this.pendingIdentifier = normalizedIdentifier;
    } else {
      this.pendingSignupIdentifier = normalizedIdentifier;
    }
  }

  login(identifier: string, password: string): { success: true } | { success: false; error: string } {
    const normalizedIdentifier = identifier.trim().toLowerCase();
    if (!normalizedIdentifier || !password) {
      return { success: false, error: 'Mobile number or email and password are required.' };
    }

    this.persistSession(normalizedIdentifier);
    this.authenticated.set(true);
    return { success: true };
  }

  verifyOtp(identifier: string, otp: string): Observable<VerifyOtpResponse> {
    return this.http.put<VerifyOtpResponse>(
      `${environment.baseUrl}staff/ssp/signIn/verifyOtp`,
      {
        identifier: identifier.trim(),
        otp: Number(otp),
      },
    );
  }

  completeOtpVerification(identifier: string, staff?: Staff): void {
    this.persistSession(identifier.trim().toLowerCase(), staff);
    this.authenticated.set(true);
    this.pendingIdentifier = null;
    this.pendingSignupIdentifier = null;
  }

  getStoredProfile(): StoredProfile | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const raw = sessionStorage.getItem(AUTH_STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const stored = JSON.parse(raw);
      const identifier: string = stored.identifier ?? '';
      const isMobileIdentifier = /^\d{10}$/.test(identifier);

      return {
        identifier,
        name: stored.name ?? '',
        countryCode: stored.countryCode || '91',
        mobile: stored.mobile || (isMobileIdentifier ? identifier : ''),
        email: stored.email || (!isMobileIdentifier ? identifier : ''),
        token: stored.token ?? '',
      };
    } catch {
      return null;
    }
  }

  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
    }
    this.authenticated.set(false);
  }

  private persistSession(identifier: string, staff?: Staff): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    sessionStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({
        identifier,
        name: staff?.name ?? '',
        countryCode: staff?.contactDetails?.countryCode ?? '',
        mobile: staff?.contactDetails?.mobile ?? '',
        email: staff?.contactDetails?.email ?? '',
        token: staff?.token ?? '',
        loggedInAt: new Date().toISOString(),
      }),
    );
  }

  private readStoredAuth(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      return !!sessionStorage.getItem(AUTH_STORAGE_KEY);
    } catch {
      return false;
    }
  }
}
